import React from 'react';
import { ChevronDown } from 'lucide-react';
import { DrawerCallout } from './ui/DrawerCallout';

export type MeetingProvider = 'none' | 'google_meet' | 'microsoft_teams';

export interface MeetingProviderSelectProps {
  meetingProvider: MeetingProvider;
  onMeetingProviderChange(provider: MeetingProvider): void;
  /** settings?.google.connected — l'assegnatario ha Google Calendar connesso. */
  googleConnected: boolean;
  /** settings?.microsoft.connected — l'assegnatario ha Microsoft connesso. */
  microsoftConnected: boolean;
  /** settings?.microsoft.enabled — sync attività Microsoft attiva sul profilo assegnatario. */
  microsoftSyncEnabled: boolean;
  /** Impostazione org-level (non presente in ResolvedSyncSettings): Teams abilitato per l'org. */
  teamsServiceEnabled: boolean;
}

/** Teams disponibile per l'org e per l'assegnatario (invariata rispetto alla versione a box). */
export function isMicrosoftMeetingAvailable(microsoftConnected: boolean, teamsServiceEnabled: boolean): boolean {
  return microsoftConnected && teamsServiceEnabled;
}

/**
 * Valore della riga di proprietà "Provider meeting": la select NUDA (stile PropRow) quando
 * almeno un provider è disponibile, altrimenti il valore inerte "Nessuno" (B2 — la riga
 * esiste sempre per la videochiamata, così l'utente sa che il concetto c'è).
 *
 * La guardia sul tipo attività NON vive più qui: è il chiamante (CalendarSection) a rendere
 * la riga solo per `videochiamata`, perché la riga è un PropRow suo.
 *
 * Fonte: LeadStatusUpdateDrawer.tsx:2022-2053 (identico in StatusUpdateDrawer.tsx:1092-1120).
 */
export function MeetingProviderSelect({
  meetingProvider,
  onMeetingProviderChange,
  googleConnected,
  microsoftConnected,
  microsoftSyncEnabled,
  teamsServiceEnabled,
}: MeetingProviderSelectProps) {
  const microsoftAvailable = isMicrosoftMeetingAvailable(microsoftConnected, teamsServiceEnabled);

  if (!googleConnected && !microsoftAvailable) {
    return <span className="text-[#787774] dark:text-gray-400">Nessuno</span>;
  }

  // Il chevron è l'unico affordance di "cliccabile" che resta a una select nuda dentro
  // una riga di proprietà (la freccia nativa è tolta da `appearance-none`).
  return (
    <>
      <select
        value={meetingProvider}
        onChange={(e) => onMeetingProviderChange(e.target.value as MeetingProvider)}
        className="min-w-0 bg-transparent border-none outline-none text-[13px] text-[#37352F] dark:text-white cursor-pointer p-0 appearance-none"
      >
        <option value="none">Nessuno</option>
        {googleConnected && <option value="google_meet">Google Meet</option>}
        {microsoftAvailable && (
          <option value="microsoft_teams" disabled={!microsoftSyncEnabled}>
            Microsoft Teams
          </option>
        )}
      </select>
      <ChevronDown className="h-2.5 w-2.5 shrink-0 text-[#787774]" strokeWidth={1.5} />
    </>
  );
}

export interface MeetingProviderNoticesProps {
  meetingProvider: MeetingProvider;
  microsoftConnected: boolean;
  microsoftSyncEnabled: boolean;
  teamsServiceEnabled: boolean;
}

/**
 * I due messaggi Teams della select (B2): come `DrawerCallout tone="warning"`, mai come
 * testo sciolto. Testi invariati rispetto alla versione precedente.
 */
export function MeetingProviderNotices({
  meetingProvider,
  microsoftConnected,
  microsoftSyncEnabled,
  teamsServiceEnabled,
}: MeetingProviderNoticesProps) {
  const microsoftAvailable = isMicrosoftMeetingAvailable(microsoftConnected, teamsServiceEnabled);
  const showTeamsDisabled = microsoftAvailable && !microsoftSyncEnabled;
  const showTeamsLocked = meetingProvider === 'microsoft_teams';
  if (!showTeamsDisabled && !showTeamsLocked) return null;

  return (
    <>
      {showTeamsDisabled && (
        <DrawerCallout tone="warning">
          Attiva la sincronizzazione attività sul calendario Microsoft nelle impostazioni per usare Teams.
        </DrawerCallout>
      )}
      {showTeamsLocked && (
        <DrawerCallout tone="warning">
          Il link Teams non può essere rimosso dall'evento dopo la creazione.
        </DrawerCallout>
      )}
    </>
  );
}
